import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import logo from '@/assets/logo.png';

const PHONE_NUMBER = '+919633210606';

interface NavbarProps {
  defaultColor?: 'white' | 'black';
}

const Navbar = ({ defaultColor = 'white' }: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/#home', label: 'Home', isExternal: false },
    { href: '/vehicles', label: 'Vehicles', isExternal: true },
    { href: '/packages', label: 'Packages', isExternal: true },
    { href: '/#services', label: 'Services', isExternal: false },
    { href: '/#contact', label: 'Contact', isExternal: false },
  ];

  const getTextColor = () => {
    if (isScrolled) return 'text-gray-700';
    return defaultColor === 'white' ? 'text-white/90' : 'text-gray-900';
  };

  const getLogoColor = () => {
    if (isScrolled) return 'text-black';
    return defaultColor === 'white' ? 'text-white' : 'text-gray-900';
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
        ? 'bg-white shadow-lg py-2'
        : 'bg-transparent py-4'
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src={logo} alt="Friends On Road Cab" className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover" />
            <div className="flex flex-col">
              <span className={`font-bold text-lg leading-tight whitespace-nowrap transition-colors ${getLogoColor()}`}>
                Friends On Road Cab
              </span>
              <span className="text-xs hidden sm:block text-primary">
                Kannur, Kerala
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isExternal ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium hover:text-primary transition-colors uppercase tracking-wide ${getTextColor()}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium hover:text-primary transition-colors uppercase tracking-wide ${getTextColor()}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="border-primary text-primary hover:bg-primary hover:text-black"
              asChild
            >
              <a href={`tel:${PHONE_NUMBER}`}>
                <Phone className="h-4 w-4" />
                Call Now
              </a>
            </Button>
            <Button
              variant="whatsapp"
              size="sm"
              className="font-bold"
              asChild
            >
              <a
                href={`https://wa.me/${PHONE_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(
                  'Hello, I want to book a taxi from Friends On Road Cab, Kannur.'
                )}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp
              </a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 hover:text-primary transition-colors ${getLogoColor()}`}
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg animate-fade-in border-t border-gray-100">
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                link.isExternal ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-base font-medium text-gray-800 hover:text-primary transition-colors uppercase tracking-wide"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setIsOpen(false)}
                    className="block py-2 text-base font-medium text-gray-800 hover:text-primary transition-colors uppercase tracking-wide"
                  >
                    {link.label}
                  </a>
                )
              ))}
              <div className="pt-4 space-y-3 border-t border-gray-100">
                <Button
                  variant="outline"
                  className="w-full border-primary text-primary hover:bg-primary hover:text-black"
                  asChild
                >
                  <a href={`tel:${PHONE_NUMBER}`}>
                    <Phone className="h-4 w-4" />
                    Call Now
                  </a>
                </Button>
                <Button
                  variant="whatsapp"
                  className="w-full font-bold"
                  asChild
                >
                  <a
                    href={`https://wa.me/${PHONE_NUMBER.replace(/\+/g, '')}?text=${encodeURIComponent(
                      'Hello, I want to book a taxi from Friends On Road Cab, Kannur.'
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Book on WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
